import { createSelector } from "reselect";
import capitalize from "lodash/capitalize";
import { AppState } from "..";
import type { AdminHtmlState } from "../../interfaces";
import type { Alert } from "src/interfaces/alert.interface";
import type { User } from "src/interfaces/systemState.interface";
import {
  getUnreviewedReturnSales,
  getUnreviewedSales,
  etsySalesWithAllocationErrorSelector,
} from "src/store/sale/selector";
import { selectUser } from "src/store/system/selector";
import {
  getNewSyncSalesDownloads,
  getNewSyncInventoryDownloads,
  pendingInventoryExistsSelector,
} from "src/store/uploads/selector";
import { getNewErrorAlerts } from "src/store/errorAlerts/selector";
import { getTransactions } from "src/store/transaction/selector";
import {
  getEBayIntegrations,
  getPoshmarkIntegrations,
  getAmazonIntegrations,
  getPlaidIntegrations,
  getEtsyIntegrations,
} from "src/store/plaidIntegration/selector";
import { getUserEmailSync } from "src/config/storage";

const getAdminHtmlState = (state: AppState) => state.adminHtml;

export const adminHtmlSelector = createSelector(
  [getAdminHtmlState],
  (adminHtml: AdminHtmlState) => adminHtml,
);

export const activeDialogSelector = createSelector(
  [adminHtmlSelector],
  ({ activeDialog }) => activeDialog || "",
);
export const activeDialogPropsSelector = createSelector(
  [adminHtmlSelector],
  ({ activeDialogProps }) => activeDialogProps,
);

export function buildZendeskLink(
  user: User,
  subject: string,
  description: string,
  errorAlertId: string,
  topic?: string,
): string {
  const email = getUserEmailSync();
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  const inventory =
    user.inventoryType === "cash"
      ? "cash_inventory_method"
      : "accrual_inventory_method";
  const plan =
    user.plan === "ultimate"
      ? "ultimate"
      : user.plan === "premium"
        ? "premium"
        : "basic";
  return (
    "https://support.myresellergenie.com/hc/en-us/requests/new?" +
    new URLSearchParams({
      tf_anonymous_requester_email: email,
      tf_33056527918491: name,
      tf_subject: subject,
      tf_description: description,
      tf_33056851616411: topic ? topic : "other",
      tf_33056407563803: inventory,
      tf_33056476777243: plan,
      tf_33117710457115: errorAlertId,
    }).toString()
  );
}

export const alertsSelector = createSelector(
  [
    selectUser,
    getUnreviewedSales,
    getNewSyncSalesDownloads,
    getNewSyncInventoryDownloads,
    pendingInventoryExistsSelector,
    getNewErrorAlerts,
    getTransactions,
    getUnreviewedReturnSales,
    getEBayIntegrations,
    getPoshmarkIntegrations,
    getAmazonIntegrations,
    getPlaidIntegrations,
    getEtsyIntegrations,
    etsySalesWithAllocationErrorSelector,
    (state: AppState) => state.userDataLoad.unreviewedPresence,
  ],
  (
    user,
    unreviewedSales,
    newSyncSalesDownloads,
    newSyncInventoryDownloads,
    pendingInventoryExists,
    errorAlerts,
    transactionState,
    unreviewedReturns,
    ebayIntegrations,
    poshmarkIntegrations,
    amazonIntegrations,
    plaidIntegrations,
    etsyIntegrations,
    etsySalesWithAllocationError,
    unreviewedPresence,
  ): Alert[] => {
    const alerts: Alert[] = [];

    for (const integration of plaidIntegrations) {
      if (!integration.error) continue;
      alerts.push({
        integrationId: integration.id,
        type: "plaid_error",
        message: `Your ${
          integration.account_name || capitalize(integration.type)
        } integration has been disconnected. Click here to reconnect.`,
      });
    }

    if (unreviewedSales.length || unreviewedPresence.sales)
      alerts.push({
        type: "review_sales",
        message: "Click here to review new sales.",
      });

    if (newSyncSalesDownloads.length)
      alerts.push({
        type: "sales",
        message: "You have new eBay transactions ready to import.",
      });

    const usePendingInventoryCollection =
      user?.featureFlags?.pendingInventory === true;
    if (
      usePendingInventoryCollection
        ? pendingInventoryExists
        : newSyncInventoryDownloads.length
    )
      alerts.push({
        type: "inventory",
        message: "Click here to review pending inventory.",
      });

    if (errorAlerts?.length) {
      for (const errorAlert of errorAlerts) {
        if (errorAlert.type === "ebay-rate-limit") {
          if (!user) continue;
          alerts.push({
            type: "error-link",
            message: errorAlert.message,
            id: errorAlert.id,
            href: buildZendeskLink(
              user,
              "eBay Setup Help",
              "Hello, can you help me get my eBay data imported? I received a notification that one of my imports contained too many items to complete on my end.",
              errorAlert.id,
            ),
          });
        } else if (errorAlert.type === "etsy-no-shop-error") {
          alerts.push({
            type: "etsy-no-shop-error",
            message:
              errorAlert.displayMessage ||
              "We couldn't find a shop associated with the Etsy account you connected. Make sure to sign into your seller account with a shop in good standing. Click here to reconnect.",
            id: errorAlert.id,
          });
        } else if (
          errorAlert.type === "general-error-notification-form-submission"
        ) {
          if (!user) continue;
          const subject =
            errorAlert.formSubject ||
            [errorAlert.platform, "Error Message"].filter(Boolean).join(" ");
          const description =
            errorAlert.formDescription ||
            [
              "Hello, I received a notification that there was an error related to my",
              errorAlert.platform,
              "integration. Can you look into this for me?",
            ]
              .filter(Boolean)
              .join(" ");

          alerts.push({
            type: "error-link",
            message:
              errorAlert.displayMessage ||
              [
                "There was an error importing your",
                errorAlert.platform,
                "data. Click here to message support for a resolution.",
              ]
                .filter(Boolean)
                .join(" "),
            id: errorAlert.id,
            href: buildZendeskLink(user, subject, description, errorAlert.id),
          });
        } else {
          alerts.push({
            type: "error",
            message: errorAlert.message,
            id: errorAlert.id,
          });
        }
      }
    }

    if (
      transactionState.unreviewedTransactions.length ||
      unreviewedPresence.transactions
    )
      alerts.push({
        type: "expenses",
        message: "Click here to review new transactions.",
      });

    if (unreviewedReturns?.length > 0 || unreviewedPresence.returns)
      alerts.push({
        type: "returns",
        message: "Click here to review new returns/refunds/cancellations.",
      });

    for (const integration of ebayIntegrations) {
      if (!integration.error) continue;
      alerts.push({
        integrationId: integration.id,
        type: "ebay_error",
        message: `My Reseller Genie has stopped syncing with your eBay ${
          integration.id === "ebay" ? "" : `${integration.name} `
        }account. Click here to restore the connection.`,
      });
    }

    for (const integration of amazonIntegrations) {
      if (!integration.error) continue;
      alerts.push({
        integrationId: integration.id,
        type: "amazon_error",
        message: `My Reseller Genie has stopped syncing with your ${
          integration.username || ""
        } Amazon account. Click here to restore the connection.`,
      });
    }

    for (const integration of poshmarkIntegrations) {
      if (integration.error) {
        alerts.push({
          integrationId: integration.id,
          type: "poshmark_error",
          message: `My Reseller Genie has stopped syncing with your ${integration.username} Poshmark account. Click here to restore the connection.`,
        });
      }

      if (
        !integration.deductionsRemindLater ||
        integration.deductionsRemindLater.toDate() < new Date()
      ) {
        alerts.push({
          type: "poshmark_deductions",
          message:
            "There may be some deductions that you’re missing for Poshmark shipping that are not shown on Poshmark’s website. Click here to review them.",
        });
      }
    }

    const hasEtsyNoShopAlert = (errorAlerts ?? []).some(
      (alert) => alert.type === "etsy-no-shop-error",
    );

    for (const integration of etsyIntegrations) {
      if (!integration.error) continue;
      // The "no shop" alert already prompts the user to reconnect, so don't
      // also show the generic Etsy sync-error alert alongside it.
      if (hasEtsyNoShopAlert) continue;
      alerts.push({
        integrationId: integration.id,
        type: "etsy_error",
        message: `My Reseller Genie has stopped syncing with your Etsy account. Click here to restore the connection.`,
      });
    }

    for (const sale of etsySalesWithAllocationError) {
      alerts.push(
        sale.is_return
          ? {
              type: "etsy-incorrect-refund-total",
              message:
                "A refund from Etsy imported with incorrect refund total. Click here to update the sale.",
              docId: sale.id,
            }
          : {
              type: "etsy-incorrect-total",
              message:
                "A sale from Etsy imported with incomplete information. Click here to update the sale.",
              docId: sale.id,
            },
      );
    }

    return alerts;
  },
);
